import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Edit2, Eye, EyeOff, Lock, LogOut, Shield } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { useLocation } from "wouter";

type UserManagementContentProps = {
  showHeader?: boolean;
};

export default function UserManagementContent({ showHeader = true }: UserManagementContentProps) {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const [, setLocation] = useLocation();
  const { data: users = [], isLoading } = trpc.users.list.useQuery();
  const { data: roles = [] } = trpc.roles.list.useQuery();

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: "", email: "", role: "user" as "user" | "admin", roleId: undefined as number | undefined });
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const createMutation = trpc.users.create.useMutation({
    onSuccess: () => {
      utils.users.list.invalidate();
      setIsOpen(false);
      setFormData({ name: "", email: "", role: "user", roleId: undefined });
      toast.success("Usuario criado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao criar usuario")),
  });

  const updateMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      utils.users.list.invalidate();
      setEditingId(null);
      setFormData({ name: "", email: "", role: "user", roleId: undefined });
      toast.success("Usuario atualizado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao atualizar usuario")),
  });

  const deleteMutation = trpc.users.delete.useMutation({
    onSuccess: () => {
      utils.users.list.invalidate();
      toast.success("Usuario deletado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao deletar usuario")),
  });

  const toggleActiveMutation = trpc.users.toggleActive.useMutation({
    onSuccess: () => {
      utils.users.list.invalidate();
      toast.success("Status do usuario atualizado!");
    },
  });

  const changePasswordMutation = trpc.users.changePassword.useMutation({
    onSuccess: (res) => {
      setNewPassword("");
      toast.success("Senha alterada com sucesso!");
      // A rotação dissolve os vínculos de troca de workspace do usuário (por
      // segurança) — avisar para o admin não achar que algo quebrou sozinho.
      if (res.dissolvedLinks) {
        toast.info(
          "Os vínculos de contas deste usuário foram desfeitos por segurança. Ele pode revincular em Contas vinculadas."
        );
      }
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao alterar senha")),
  });

  const handleSave = () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Preencha todos os campos");
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, name: formData.name, email: formData.email, role: formData.role, roleId: formData.roleId ?? null });
    } else {
      createMutation.mutate({ name: formData.name, email: formData.email, role: formData.role, roleId: formData.roleId });
    }
  };

  const allIds = users.map((u: any) => u.id);
  const allSelected = allIds.length > 0 && allIds.every((id: number) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const toggleOne = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkDelete = () => {
    if (!confirm(`Excluir ${selectedIds.size} usuario(s)? Esta acao nao pode ser desfeita.`)) return;
    selectedIds.forEach((id) => deleteMutation.mutate({ id }));
    setSelectedIds(new Set());
  };

  const getRoleBadgeVariant = (roleName: string | null | undefined) => {
    if (!roleName) return "secondary" as const;
    const slug = roleName.toLowerCase();
    if (slug.includes("admin")) return "default" as const;
    if (slug.includes("marketing")) return "default" as const;
    if (slug.includes("comercial")) return "outline" as const;
    return "secondary" as const;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {showHeader ? (
          <div>
            <h2 className="text-2xl font-bold">Gerenciamento de Usuarios</h2>
            <p className="text-muted-foreground text-sm mt-1">Gerencie os usuarios e seus perfis de acesso</p>
          </div>
        ) : <div />}
        <div className="flex flex-wrap items-center gap-2">
          {someSelected && (
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deleteMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Excluir selecionados ({selectedIds.size})
            </Button>
          )}
          {can("users:manage_roles") && (
            <Button variant="outline" onClick={() => setLocation("/roles")}>
              <Shield className="w-4 h-4 mr-2" />
              Perfis de Acesso
            </Button>
          )}
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); setFormData({ name: "", email: "", role: "user", roleId: undefined }); }}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar Usuario" : "Novo Usuario"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nome</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">E-mail</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Perfil de Acesso</label>
                  <Select
                    value={formData.roleId?.toString() ?? ""}
                    onValueChange={(value) => {
                      const roleId = Number(value);
                      const role = roles.find((r: any) => r.id === roleId);
                      setFormData({
                        ...formData,
                        roleId,
                        role: role?.slug === "admin" ? "admin" : "user",
                      });
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione um perfil" /></SelectTrigger>
                    <SelectContent>
                      {roles.map((role: any) => (
                        <SelectItem key={role.id} value={role.id.toString()}>
                          <div className="flex items-center gap-2">
                            {role.name}
                            {role.isSystem && <Badge variant="outline" className="text-[10px] px-1 py-0">Sistema</Badge>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.roleId && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {roles.find((r: any) => r.id === formData.roleId)?.description}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingId ? "Atualizar" : "Criar"}
                  </Button>
                  <Button variant="outline" onClick={() => setIsOpen(false)}>Cancelar</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Usuarios da Plataforma</CardTitle>
              <CardDescription>
                {someSelected
                  ? `${selectedIds.size} de ${users.length} selecionados`
                  : `Total de ${users.length} usuarios`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando usuarios...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhum usuario cadastrado</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Selecionar todos"
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ultimo Login</TableHead>
                    <TableHead className="text-right">Acoes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user: any) => (
                    <TableRow
                      key={user.id}
                      className={selectedIds.has(user.id) ? "bg-muted/40" : undefined}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(user.id)}
                          onCheckedChange={() => toggleOne(user.id)}
                          aria-label={`Selecionar ${user.name}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(user.roleName)}>
                          {user.roleName || (user.tenantRole === "owner" ? "Owner" : user.role === "admin" ? "Admin" : "Membro")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isActive ? "outline" : "destructive"}>
                          {user.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.lastSignedIn ? new Date(user.lastSignedIn).toLocaleDateString("pt-BR") : "Nunca"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFormData({ name: user.name, email: user.email, role: user.role, roleId: user.roleId ?? undefined })}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader><DialogTitle>Editar Usuario</DialogTitle></DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <label className="text-sm font-medium">Nome</label>
                                  <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium">E-mail</label>
                                  <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Perfil de Acesso</label>
                                  <Select
                                    value={formData.roleId?.toString() ?? ""}
                                    onValueChange={(value) => {
                                      const roleId = Number(value);
                                      const role = roles.find((r: any) => r.id === roleId);
                                      setFormData({
                                        ...formData,
                                        roleId,
                                        role: role?.slug === "admin" ? "admin" : "user",
                                      });
                                    }}
                                  >
                                    <SelectTrigger><SelectValue placeholder="Selecione um perfil" /></SelectTrigger>
                                    <SelectContent>
                                      {roles.map((role: any) => (
                                        <SelectItem key={role.id} value={role.id.toString()}>
                                          {role.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => updateMutation.mutate({ id: user.id, name: formData.name, email: formData.email, role: formData.role, roleId: formData.roleId ?? null })}
                                    disabled={updateMutation.isPending}
                                  >
                                    Atualizar
                                  </Button>
                                  <Button variant="outline">Cancelar</Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Lock className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader><DialogTitle>Alterar Senha</DialogTitle></DialogHeader>
                              <div className="space-y-4">
                                <p className="text-sm text-muted-foreground">Usuario: {user.name}</p>
                                <div>
                                  <label className="text-sm font-medium">Nova Senha</label>
                                  <div className="flex gap-2">
                                    <Input
                                      type={showPassword ? "text" : "password"}
                                      value={newPassword}
                                      onChange={(e) => setNewPassword(e.target.value)}
                                      placeholder="Digite a nova senha"
                                    />
                                    <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)}>
                                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => {
                                      if (!newPassword.trim()) { toast.error("Digite uma senha"); return; }
                                      changePasswordMutation.mutate({ userId: user.id, newPassword });
                                    }}
                                    disabled={changePasswordMutation.isPending}
                                  >
                                    Alterar Senha
                                  </Button>
                                  <Button variant="outline">Cancelar</Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActiveMutation.mutate({ userId: user.id })}
                            title={user.isActive ? "Desativar usuario" : "Ativar usuario"}
                          >
                            <LogOut className="w-4 h-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => {
                              if (confirm("Excluir este usuario?")) deleteMutation.mutate({ id: user.id });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
